/**
 * 地图点位管理器
 * 用于在 Cesium 地图上高效显示大量点位
 */

declare const Cesium: any;

export interface MapPointData {
  id: string | number;
  name: string;
  lng: number | string;
  lat: number | string;
  h?: number | string;
  type?: string;
  [key: string]: any;
}

export interface MapPointsManagerOptions {
  iconUrl?: string;
  iconScale?: number;
  labelFont?: string;
  labelColor?: string;
  labelBackgroundColor?: string;
  labelShowHeight?: number; // 标签显示的相机高度阈值（米）
  onClick?: (point: MapPointData) => void;
}

export class MapPointsManager {
  private viewer: any;
  private dataSource: any;
  private handler: any;
  private options: MapPointsManagerOptions;
  private pointsMap: Map<string | number, any> = new Map();
  private batchShowTimer: any = null; // 分批显示的定时器

  constructor(viewer: any, options: MapPointsManagerOptions = {}) {
    this.viewer = viewer;
    this.options = {
      iconScale: 1,
      labelFont: "14px sans-serif",
      labelColor: "#ffffff",
      labelBackgroundColor: "rgba(0, 0, 0, 0.7)",
      labelShowHeight: 5000,
      ...options,
    };
    this.init();
  }

  private init() {
    this.dataSource = new Cesium.CustomDataSource("mapPoints");
    this.viewer.dataSources.add(this.dataSource);
    if (this.options.onClick) {
      this.setupClickHandler();
    }
  }

  private setupClickHandler() {
    this.handler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);
    this.handler.setInputAction((event: any) => {
      const pickedObject = this.viewer.scene.pick(event.position);
      if (Cesium.defined(pickedObject) && pickedObject.id) {
        const entity = pickedObject.id;
        if (entity.pointData && this.options.onClick) {
          this.options.onClick(entity.pointData);
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  addPoints(points: MapPointData[]) {
    if (!points || points.length === 0) return;
    points.forEach((point) => this.addPoint(point));
  }

  addPoint(point: MapPointData) {
    if (!point.lng || !point.lat) return;
    const lng = Number(point.lng);
    const lat = Number(point.lat);
    const h = Number(point.h || 0);
    if (isNaN(lng) || isNaN(lat)) return;
    if (this.pointsMap.has(point.id)) {
      this.removePoint(point.id);
    }
    const position = Cesium.Cartesian3.fromDegrees(lng, lat, h);
    // 使用 distanceDisplayCondition 让 Cesium 自动根据距离控制显示
    const labelDisplayCondition = new Cesium.DistanceDisplayCondition(
      0,
      this.options.labelShowHeight
    );

    const entityOptions: any = {
      position: position,
      pointData: point,
      billboard: {
        image: this.options.iconUrl,
        scale: this.options.iconScale,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      label: {
        text: point.name || "",
        font: this.options.labelFont,
        fillColor: Cesium.Color.fromCssColorString(this.options.labelColor!),
        backgroundColor: Cesium.Color.fromCssColorString(this.options.labelBackgroundColor!),
        showBackground: true,
        backgroundPadding: new Cesium.Cartesian2(6, 4),
        pixelOffset: new Cesium.Cartesian2(0, -35),
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: labelDisplayCondition,
      },
    };
    const entity = this.dataSource.entities.add(entityOptions);
    this.pointsMap.set(point.id, entity);
  }

  removePoint(id: string | number) {
    const entity = this.pointsMap.get(id);
    if (entity) {
      this.dataSource.entities.remove(entity);
      this.pointsMap.delete(id);
    }
  }

  clearAll() {
    this.dataSource.entities.removeAll();
    this.pointsMap.clear();
  }

  /**
   * 显示所有点位（立即显示）
   */
  show() {
    if (this.dataSource) {
      this.dataSource.show = true;
    }
  }

  /**
   * 分批显示所有点位（渐进式显示，避免卡顿）
   */
  showBatch(batchSize: number = 10) {
    if (!this.dataSource) return;

    // 取消之前的分批显示任务
    if (this.batchShowTimer) {
      clearTimeout(this.batchShowTimer);
      this.batchShowTimer = null;
    }

    // 先隐藏所有点位
    this.dataSource.show = false;

    // 获取所有实体
    const entities = Array.from(this.pointsMap.values());
    const totalCount = entities.length;

    if (totalCount === 0) {
      this.dataSource.show = true;
      return;
    }

    // 先隐藏所有实体
    entities.forEach(entity => {
      entity.show = false;
    });

    // 显示 dataSource
    this.dataSource.show = true;

    let currentIndex = 0;

    const showNextBatch = () => {
      if (currentIndex >= totalCount) {
        return;
      }

      const endIndex = Math.min(currentIndex + batchSize, totalCount);
      
      // 显示当前批次的实体
      for (let i = currentIndex; i < endIndex; i++) {
        entities[i].show = true;
      }

      currentIndex = endIndex;

      // 使用 requestIdleCallback 或 setTimeout 继续下一批
      if (typeof requestIdleCallback !== 'undefined') {
        this.batchShowTimer = requestIdleCallback(() => showNextBatch(), { timeout: 100 });
      } else {
        this.batchShowTimer = setTimeout(showNextBatch, 16); // 约 60fps
      }
    };

    // 开始显示第一批
    showNextBatch();
  }

  /**
   * 隐藏所有点位
   */
  hide() {
    // 取消分批显示任务
    if (this.batchShowTimer) {
      clearTimeout(this.batchShowTimer);
      this.batchShowTimer = null;
    }

    if (this.dataSource) {
      this.dataSource.show = false;
    }
  }

  /**
   * 设置点位可见性
   */
  setVisible(visible: boolean) {
    if (this.dataSource) {
      this.dataSource.show = visible;
    }
  }

  /**
   * 获取当前可见性状态
   */
  isVisible(): boolean {
    return this.dataSource ? this.dataSource.show : false;
  }

  updatePoints(points: MapPointData[]) {
    this.clearAll();
    this.addPoints(points);
  }

  flyToPoint(id: string | number, height: number = 1000) {
    const entity = this.pointsMap.get(id);
    if (entity) {
      this.viewer.flyTo(entity, {
        offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-45), height),
      });
    }
  }

  flyToAll() {
    if (this.dataSource.entities.values.length > 0) {
      this.viewer.flyTo(this.dataSource);
    }
  }

  getCount(): number {
    return this.pointsMap.size;
  }

  destroy() {
    // 清除分批显示定时器
    if (this.batchShowTimer) {
      clearTimeout(this.batchShowTimer);
      this.batchShowTimer = null;
    }

    if (this.handler) {
      this.handler.destroy();
      this.handler = null;
    }
    this.clearAll();
    if (this.dataSource) {
      this.viewer.dataSources.remove(this.dataSource);
      this.dataSource = null;
    }
    this.pointsMap.clear();
  }
}

export default MapPointsManager;
